// ═══════════════════════════════════════════════════════════════
// Wallace Global Fund — site JS  (native scroll; ScrollTrigger-driven)
// ═══════════════════════════════════════════════════════════════

document.documentElement.classList.add('js');

document.addEventListener('DOMContentLoaded', () => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

  // ── Word-by-word scroll highlight (colour scrubs to scroll) ──
  // `fromColor` is optional — when omitted the muted start state comes
  // from the `.hl-word` rule in CSS.
  const splitWords = (el) => {
    const words = el.textContent.trim().split(/\s+/);
    const frag = document.createDocumentFragment();
    words.forEach((w, i) => {
      const span = document.createElement('span');
      span.className = 'hl-word';
      span.textContent = w;
      frag.appendChild(span);
      if (i < words.length - 1) frag.appendChild(document.createTextNode(' '));
    });
    el.textContent = '';
    el.appendChild(frag);
    return el.querySelectorAll('.hl-word');
  };

  // Paragraphs sharing a `data-hl-group` scrub one after another off a
  // single trigger, instead of each running its own overlapping range —
  // otherwise stacked paragraphs fill at the same time. Each segment's
  // length follows its word count, so a long paragraph gets proportionally
  // more of the scroll distance.
  const scrollHighlightGroup = (els, container, startAt, endAt) => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: container,
        start: startAt || 'top 78%',
        end: endAt || 'bottom 60%',
        scrub: true
      }
    });

    els.forEach((el) => {
      const targets = splitWords(el);
      const toColor = el.getAttribute('data-hl-to');
      const fromColor = el.getAttribute('data-hl-from');
      const vars = { color: toColor, ease: 'none', stagger: 0.6 };
      if (fromColor) tl.fromTo(targets, { color: fromColor }, vars);
      else tl.to(targets, vars);
    });

    return tl;
  };

  const scrollHighlight = (el, toColor, fromColor, startAt, endAt) => {
    const targets = splitWords(el);
    const vars = {
      color: toColor,
      ease: 'none',
      stagger: 0.6,
      scrollTrigger: {
        trigger: el,
        start: startAt || 'top 82%',
        end: endAt || 'bottom 48%',
        scrub: true
      }
    };

    if (fromColor) gsap.fromTo(targets, { color: fromColor }, vars);
    else gsap.to(targets, vars);
  };

  if (window.gsap && window.ScrollTrigger && !reduce) {
    // orange leads: #826551 → #ff954d
    document.querySelectorAll('.article__lead').forEach((el) => scrollHighlight(el, '#ff954d'));
    // closing card copy: white 60% → white 100%
    document.querySelectorAll('.closing-card__text').forEach((el) => scrollHighlight(el, 'rgba(255,255,255,1)'));
    // story copy: colours — and optionally the scrub range — declared per
    // element in the markup. Copy that sits near the page bottom needs an
    // earlier `end` or the last words never reach full colour.
    const groups = new Map();
    document.querySelectorAll('[data-hl-to]').forEach((el) => {
      const name = el.getAttribute('data-hl-group');
      if (name) {
        if (!groups.has(name)) groups.set(name, []);
        groups.get(name).push(el);
        return;
      }
      scrollHighlight(
        el,
        el.getAttribute('data-hl-to'),
        el.getAttribute('data-hl-from'),
        el.getAttribute('data-hl-start'),
        el.getAttribute('data-hl-end')
      );
    });

    // Grouped copy shares one trigger spanning its section, so the
    // paragraphs fill in document order rather than together.
    groups.forEach((els) => {
      const first = els[0];
      const container = first.closest('[data-hl-scope]') || first.closest('section') || first.parentElement;
      scrollHighlightGroup(
        els,
        container,
        first.getAttribute('data-hl-start'),
        first.getAttribute('data-hl-end')
      );
    });

    // Lazy images and the swapped-in webfonts both shift layout after the
    // triggers are measured — without this the scrub ranges drift by a
    // couple of hundred pixels between loads.
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
  }

  // ── Scroll progress bar (fills left → right down the page) ──
  const progress = document.querySelector('.scroll-progress__bar');
  if (progress) {
    let ticking = false;
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
      progress.style.transform = 'scaleX(' + p + ')';
      ticking = false;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    update();
  }

  // ── Gallery: two rows slide opposite ways, scrubbed to scroll ──
  const rows = document.querySelectorAll('[data-gallery-row]');
  if (rows.length && window.gsap && window.ScrollTrigger && !reduce) {
    const base = () => -window.innerWidth * 0.165;
    const amp  = () => window.innerWidth * 0.085;
    rows.forEach((row) => {
      const dir = parseFloat(row.dataset.galleryRow) || 1;
      gsap.fromTo(row,
        { x: () => base() - amp() * dir },
        {
          x: () => base() + amp() * dir,
          ease: 'none',
          scrollTrigger: {
            trigger: row.closest('.gallery'),
            start: 'top bottom',
            end: 'bottom top',
            scrub: 0.4,
            invalidateOnRefresh: true
          }
        });
    });
  }

  // Shared by the carousel and reveal maths below
  const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

  // ── Movements carousel — arrows + drag over a finite card list ──
  // Finite, not looping: three-ish items read as a list with a start and
  // an end, so it clamps at the bounds with a rubber-band instead of
  // wrapping. Release snaps to the nearest card.
  document.querySelectorAll('[data-movements]').forEach((root) => {
    const viewport = root.querySelector('.movements__viewport');
    const track = root.querySelector('.movements__track');
    const prev = root.querySelector('[data-movements-prev]');
    const next = root.querySelector('[data-movements-next]');
    if (!viewport || !track || reduce) return;

    const cards = Array.prototype.slice.call(track.children);
    if (!cards.length) return;

    let x = 0;
    let target = 0;
    let limit = 0;
    let inset = 0;
    let dragging = false;
    let lastPointerX = 0;
    let vel = 0;
    let frame = 0;
    let running = false;

    const measure = () => {
      inset = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      limit = Math.max(0, track.scrollWidth - viewport.clientWidth);
      target = clamp(target, -limit, 0);
      x = clamp(x, -limit, 0);
      root.classList.toggle('is-draggable', limit > 0);
    };

    // Index of the card nearest the left edge for a given offset
    const nearest = (offset) => {
      let best = 0;
      let bestGap = Infinity;
      for (let i = 0; i < cards.length; i++) {
        const gap = Math.abs((cards[i].offsetLeft - inset) + offset);
        if (gap < bestGap) { bestGap = gap; best = i; }
      }
      return best;
    };

    const goTo = (i) => {
      const card = cards[clamp(i, 0, cards.length - 1)];
      target = clamp(-(card.offsetLeft - inset), -limit, 0);
      start();
    };

    const syncArrows = () => {
      if (prev) prev.disabled = target >= -1;
      if (next) next.disabled = target <= -limit + 1;
    };

    const render = () => {
      if (!dragging) {
        // Ease toward the snap target; stop once it has settled.
        x += (target - x) * 0.14;
        if (Math.abs(target - x) < 0.4) {
          x = target;
          track.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
          running = false;
          return;
        }
      }
      track.style.transform = 'translate3d(' + x.toFixed(2) + 'px,0,0)';
      frame = requestAnimationFrame(render);
    };

    const start = () => {
      syncArrows();
      if (!running) { running = true; frame = requestAnimationFrame(render); }
    };

    // ── Drag ──
    root.addEventListener('pointerdown', (e) => {
      if (limit <= 0 || e.target.closest('.movements__arrow, a')) return;
      dragging = true;
      lastPointerX = e.clientX;
      vel = 0;
      root.classList.add('is-dragging');
      if (root.setPointerCapture) root.setPointerCapture(e.pointerId);
      start();
    });

    root.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastPointerX;
      lastPointerX = e.clientX;
      // Rubber-band: halve the pull once past either end.
      const past = x > 0 || x < -limit;
      x += past ? dx * 0.35 : dx;
      vel = dx;
    });

    const release = () => {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      // Project where the flick was heading, then snap to that card.
      target = clamp(x + vel * 6, -limit, 0);
      goTo(nearest(target));
    };
    root.addEventListener('pointerup', release);
    root.addEventListener('pointercancel', release);

    // Suppress the click that ends a drag, so links do not fire on a flick
    root.addEventListener('click', (e) => {
      if (Math.abs(vel) > 2) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    // ── Arrows + keyboard ──
    if (prev) prev.addEventListener('click', () => goTo(nearest(target) - 1));
    if (next) next.addEventListener('click', () => goTo(nearest(target) + 1));

    root.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(nearest(target) - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(nearest(target) + 1); }
    });

    // Keep a focused card in view when tabbing through the links
    track.addEventListener('focusin', (e) => {
      const card = e.target.closest('.movement-card');
      if (card) goTo(cards.indexOf(card));
    });

    measure();
    syncArrows();
    track.style.transform = 'translate3d(0,0,0)';

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { measure(); goTo(nearest(target)); }, 150);
    }, { passive: true });
  });

  // ── Practices — tabs with a swapping circular image ──
  // One tab is always open: clicking the open one leaves it open rather
  // than collapsing to an empty figure. CSS animates the panel height,
  // so this only moves state and cross-fades the matching image.
  document.querySelectorAll('[data-accordion]').forEach((root) => {
    const items = Array.prototype.slice.call(root.querySelectorAll('.accordion__item'));
    if (!items.length) return;

    const images = Array.prototype.slice.call(root.querySelectorAll('[data-practice-img]'));

    const show = (item) => {
      items.forEach((other) => {
        const open = other === item;
        const trigger = other.querySelector('.accordion__trigger');
        other.classList.toggle('is-open', open);
        if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      const key = item.getAttribute('data-practice');
      images.forEach((img) => {
        img.classList.toggle('is-current', img.getAttribute('data-practice-img') === key);
      });
    };

    items.forEach((item) => {
      const trigger = item.querySelector('.accordion__trigger');
      if (!trigger) return;
      trigger.addEventListener('click', () => show(item));
    });

    // Honour whichever tab the markup opened, and preload its siblings so
    // the first few swaps do not flash an empty circle.
    const initial = items.filter((i) => i.classList.contains('is-open'))[0] || items[0];
    show(initial);
    images.forEach((img) => { img.loading = 'eager'; });
  });

  // ── Scroll reveal for figures (clip wipe + zoom) ──
  // rAF-throttled so we read layout at most once per frame (Chrome-friendly).
  const reveals = Array.prototype.slice.call(document.querySelectorAll('.js-reveal'));
  if (reveals.length) {
    if (reduce) {
      reveals.forEach((el) => el.classList.add('is-in'));
    } else {
      const revealVisible = () => {
        for (let i = reveals.length - 1; i >= 0; i--) {
          const r = reveals[i].getBoundingClientRect();
          if (r.top < window.innerHeight * 0.9 && r.bottom > 0) {
            reveals[i].classList.add('is-in');
            reveals.splice(i, 1);
          }
        }
      };
      let ticking = false;
      const onScroll = () => {
        if (ticking || !reveals.length) return;
        ticking = true;
        requestAnimationFrame(() => { revealVisible(); ticking = false; });
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      revealVisible();                          // initial (above-the-fold)
      setTimeout(() => reveals.slice().forEach((el) => el.classList.add('is-in')), 4000);
    }
  }

  // ── Hero video autoplay (muted thumbnail) ──
  const heroVideo = document.querySelector('.hero__video-media');
  if (heroVideo) {
    const tryPlay = () => { const p = heroVideo.play(); if (p) p.catch(() => {}); };
    if (heroVideo.readyState >= 2) tryPlay();
    else heroVideo.addEventListener('loadeddata', tryPlay, { once: true });
  }

  // ── Video modal ──
  // A trigger carrying `data-video-src` plays that file inline; otherwise
  // it falls back to the YouTube embed via `data-youtube-id`.
  const modal = document.querySelector('.video-modal');
  if (modal) {
    const frame = modal.querySelector('.video-modal__frame');

    const openFile = (src, poster) => {
      frame.innerHTML =
        '<video class="video-modal__media" src="' + src + '" ' +
        (poster ? 'poster="' + poster + '" ' : '') +
        'controls autoplay playsinline preload="metadata" ' +
        'title="Wallace Global Fund video"></video>';
    };

    const openEmbed = (id) => {
      frame.innerHTML =
        '<iframe src="https://www.youtube.com/embed/' + id +
        '?autoplay=1&rel=0&modestbranding=1" title="Wallace Global Fund video" ' +
        'allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowfullscreen></iframe>';
    };

    const open = (btn) => {
      const src = btn.getAttribute('data-video-src');
      if (src) openFile(src, btn.getAttribute('data-video-poster'));
      else openEmbed(btn.getAttribute('data-youtube-id') || 'ScMzIvxBSi4');
      modal.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      // Pause before tearing the node down, or the audio can outlive it
      const playing = frame.querySelector('video');
      if (playing) playing.pause();
      modal.classList.remove('is-open');
      frame.innerHTML = '';
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-video-modal]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        open(btn);
      });
    });
    modal.querySelectorAll('[data-modal-close]').forEach((el) => el.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  }
});
